<template>
  <div
    class="product-card"
    :class="{ 'has-colors': colors.length }"
    data-testid="product-card"
  >
    <div class="product-card__image-wrapper">
      <slot
        name="image"
        v-bind="{
          image,
          title,
          link,
          imageHeight,
          imageWidth,
          imageTag,
          nuxtImgConfig,
        }"
      >
        <SfButton
          :link="link"
          class="sf-button--pure product-card__link"
          data-testid="product-link"
          :aria-label="'Go To Product'"
          v-on="$listeners"
        >
          <template v-if="Array.isArray(image)">
            <SfImage
              v-for="(picture, key) in image.slice(0, 2)"
              :key="key"
              class="product-card__picture"
              :src="picture"
              :alt="title"
              :width="imageWidth"
              :height="imageHeight"
              :image-tag="imageTag"
              :nuxt-img-config="nuxtImgConfig"
            />
          </template>
          <SfImage
            v-else
            class="product-card__image"
            :src="image"
            :alt="title"
            :width="imageWidth"
            :height="imageHeight"
            :image-tag="imageTag"
            :nuxt-img-config="nuxtImgConfig"
          />
        </SfButton>
      </slot>
      <slot name="badge" v-bind="{ badgeLabel, badgeColor }">
        <SfBadge
          class="product-card__badge"
          :class="[badgeColorClass, { 'display-none': !badgeLabel }]"
        >
          {{ badgeLabel }}
        </SfBadge>
      </slot>
      <SfButton
        :aria-label="`${ariaLabel} ${title}`"
        :class="[wishlistIconClasses]"
        data-testid="product-wishlist-button"
        @click="toggleIsInWishlist"
      >
        <slot name="wishlist-icon" v-bind="{ currentWishlistIcon }">
          <SvgImage
            v-if="currentWishlistIcon !== false"
            :icon="currentWishlistIcon"
            label="wishlist"
            width="24"
            height="24"
          />
        </slot>
      </SfButton>
    </div>
    <slot name="made-in" v-bind="{ madeInCanada }">
      <div
        v-if="madeInCanada"
        class="product-card__made-in"
      >
        <SvgImage
          icon="canada"
          label="Canada"
          width="20"
          height="20"
        />
      </div>
    </slot>
    <slot name="title" v-bind="{ title, link }">
      <SfButton
        :link="link"
        class="sf-button--pure product-card__link product-card__title"
        data-testid="product-link"
        v-on="$listeners"
      >
        {{ title }}
      </SfButton>
    </slot>
    <div class="bottom-info">
      <slot name="available" v-bind="{ sizes, heights }">
        <div
          v-if="sizes.length > 0 || heights.length > 0"
          class="product-card__available"
        >
          Available in:
          <span v-if="sizes.length > 0 && heights.length > 0">{{ sizes.length }} sizes, </span>
          <span v-else-if="sizes.length > 0">{{ sizes.length }} sizes</span>
          <span v-if="heights.length > 0">{{ heights.length }} heights</span>
        </div>
      </slot>
      <slot name="colors" v-bind="{ colors }">
        <div class="product-card__colors">
          <div
            v-for="(color, i) in colors.slice(0, 4)"
            :key="color.value"
            :style="{ background: swatchBg(color) }"
            class="product-card__color"
            @click="handleSelectedColor(i)"
          />
          <span v-if="colors.length > 4">+ More Options</span>
        </div>
      </slot>
      <slot name="price" v-bind="{ specialPrice, regularPrice }">
        <SfPrice
          :class="{ 'display-none': !regularPrice }"
          class="product-card__price"
          :regular="regularPrice"
          :special="specialPrice"
        />
      </slot>
      <slot name="reviews" v-bind="{ maxRating, scoreRating }">
        <div
          v-if="scoreRating"
          class="product-card__reviews"
        >
          <SfRating
            v-if="typeof scoreRating === 'number'"
            class="product-card__rating"
            :max="maxRating"
            :score="scoreRating"
          />
          <SfButton
            :class="{ 'display-none': !reviewsCount }"
            :aria-label="`Read ${reviewsCount} reviews about ${title}`"
            class="sf-button--pure product-card__reviews-count"
            data-testid="product-review-button"
            @click="$emit('click:reviews')"
          >
            ({{ reviewsCount }})
          </SfButton>
        </div>
      </slot>
      <slot name="quick-ship" v-bind="{ quickShip }">
        <div v-if="quickShip" class="product-card__quick-ship">
          <span>Quick Ship</span>
          <SvgImage
            icon="shipping-small"
            label="shipping"
            width="20"
            height="20"
          />
        </div>
      </slot>
    </div>
  </div>
</template>

<script>
import { colorsValues as SF_COLORS } from '@storefront-ui/shared/variables/colors';
import {
  SfIcon,
  SfPrice,
  SfRating,
  SfImage,
  SfCircleIcon,
  SfBadge,
  SfButton,
} from '@storefront-ui/vue';
import SvgImage from '~/components/General/SvgImage.vue';
import { useContext } from '@nuxtjs/composition-api';

export default {
  components: {
    SfPrice,
    SfRating,
    SfIcon,
    SfImage,
    SfCircleIcon,
    SfBadge,
    SfButton,
    SvgImage,
  },
  props: {
    image: {
      type: [Array, Object, String],
      default: '',
    },
    imageWidth: {
      type: [Number, String],
      default: null,
    },
    imageHeight: {
      type: [Number, String],
      default: null,
    },
    badgeLabel: {
      type: String,
      default: '',
    },
    badgeColor: {
      type: String,
      default: '',
    },
    colors: {
      type: Array,
      default: () => [],
    },
    madeInCanada: {
      type: Boolean,
      default: false,
    },
    sizes: {
      type: Array,
      default: () => [],
    },
    heights: {
      type: Array,
      default: () => [],
    },
    quickShip: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    link: {
      type: [String, Object],
      default: null,
    },
    /**
     * Link element tag
     * @deprecated will be removed in 1.0.0 use slot to replace content
     */
    linkTag: {
      type: String,
      default: undefined,
    },
    scoreRating: {
      type: [Number, Boolean],
      default: false,
    },
    reviewsCount: {
      type: [Number, Boolean],
      default: false,
    },
    maxRating: {
      type: [Number, String],
      default: 5,
    },
    regularPrice: {
      type: [Number, String],
      default: null,
    },
    specialPrice: {
      type: [Number, String],
      default: null,
    },
    wishlistIcon: {
      type: [String, Array, Boolean],
      default: 'heart',
    },
    isInWishlistIcon: {
      type: [String, Array],
      default: 'heart_fill',
    },
    isInWishlist: {
      type: Boolean,
      default: false,
    },
    isAddedToCart: {
      type: Boolean,
      default: false,
    },
    imageTag: {
      type: String,
      default: '',
    },
    nuxtImgConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { app } = useContext();

    const baseMediaUrl = `${app.$vsf.$magento.config.magentoBaseUrl}/media/attribute/swatch/swatch_thumb/110x90`;
    const swatchBg = (color) => (color?.['__typename'] === 'ImageSwatchData'
      ? `url(${baseMediaUrl}${color.value}) center / 100%`
      : `${color.value} center / 100%`);

    return {
      swatchBg,
    };
  },
  data() {
    return {
      isAddingToCart: false,
    };
  },
  computed: {
    isSFColors() {
      return SF_COLORS.includes(this.badgeColor.trim());
    },
    badgeColorClass() {
      return this.isSFColors ? `${this.badgeColor.trim()}` : '';
    },
    currentWishlistIcon() {
      return this.isInWishlist ? this.isInWishlistIcon : this.wishlistIcon;
    },
    showAddedToCartBadge() {
      return !this.isAddingToCart && this.isAddedToCart;
    },
    ariaLabel() {
      return this.isInWishlist ? 'Remove from wishlist' : 'Add to wishlist';
    },
    wishlistIconClasses() {
      const defaultClass = 'sf-button--pure product-card__wishlist-icon';
      return `${defaultClass} ${this.isInWishlist ? 'on-wishlist' : ''}`;
    },
  },
  methods: {
    toggleIsInWishlist() {
      this.$emit('click:wishlist', !this.isInWishlist);
    },
    handleSelectedColor(colorIndex) {
      if (this.colors.length > 0) {
        this.colors.forEach((color, i) => {
          if (colorIndex === i) {
            this.$emit('click:colors', color);
          }
        });
      }
    },
  },
};
</script>

<style lang="scss">
.product-card {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;
  z-index: var(--product-card-z-index);
  max-width: var(--product-card-max-width, 100%);
  flex: 0 1 var(--product-card-max-width, 100%);
  height: var(--product-card-height);
  padding: var(--product-card-padding, 0);
  background-color: var(--product-card-background, var(--c-white));
  min-width: 0;
  width: 100%;
  &:hover {
    --product-card-image-opacity: 1;
    --product-card-image-even-opacity: 1;
    --product-card-add-button-opacity: 1;
    --product-card-z-index: 1;
    --product-card-box-shadow: 0 4px 11px rgba(29, 31, 34, 0.1);
  }
  &:active {
    --product-card-box-shadow: 0 4px 11px rgba(29, 31, 34, 0.1);
  }
  .bottom-info {
    margin-bottom: 40px;
  }
  &__link {
    display: block;
    width: 100%;
    line-height: 0;
    text-decoration: none;
    margin: var(--product-card-link-margin, 0);
    text-align: left;
  }
  &__made-in {
    display: flex;
    align-items: center;
    position: absolute;
    top: 10px;
    right: 10px;
  }
  &__available {
    font-size: 12px;
    margin-top: 8px;
  }
  &__colors {
    font-size: 12px;
    margin-top: 8px;
    display: flex;
    align-items: center;
    @include for-desktop {
      pointer-events: none;
    }
  }
  &__color {
    width: 14px;
    height: 14px;
    margin: 0 6px 0 0;
    border-radius: 3px;
    pointer-events: visible;
  }
  &__quick-ship {
    display: flex;
    align-items: center;
    position: absolute;
    right: 10px;
    bottom: 42px;
    font-size: 14px;
    .svg-image {
      color: var(--c-primary);
      margin-left: 4px;
    }
    @include for-mobile {
      position: static;
      font-size: 12px;
      margin-top: 10px;
    }
  }
  &__title {
    flex: 1;
    @include font(
      --product-card-title-font,
      400,
      12px,
      normal,
      var(--font-family--secondary)
    );
    display: block;
    color: var(--product-card-title-color, var(--c-link));
    margin: var(--product-card-title-margin, 8px 0 0 0);
    min-height: 38px;
  }
  &__image-wrapper {
    position: relative;
  }
  &__image,
  &__picture {
    width: 100%;
    --image-width: var(--product-card-image-width, 100%);
    --image-height: var(--product-card-image-height, auto);
    transition: var(--product-card-image-transition, opacity 150ms ease-in-out);
    opacity: var(--product-card-image-opacity);
    &:nth-child(even) {
      position: absolute;
      top: 0;
      left: 0;
      opacity: var(--product-card-image-even-opacity, 0);
    }
    .sf-image--placeholder {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }
  &__badge {
    position: absolute;
    top: var(--product-card-badge-top, var(--spacer-xs));
    left: var(--product-card-badge-left, 0);
  }
  &__colors-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: var(--spacer-xs);
    width: 2.5rem;
    height:  2.5rem;
    padding: 0;
    color: var(--c-white);
    z-index: 2;
  }
  &__add-button {
    --circle-icon-position: absolute;
    right: var(--product-card-add-button-right, calc(100% - 40px));
    bottom: var(--product-card-add-button-bottom, 8px);
    display: var(--product-card-add-button-display, flex);
    transform: var(--product-card-add-button-transform);
    opacity: var(--product-card-add-button-opacity, 1);
    width: var(--product-card-add-button-width, 34px);
    height: var(--product-card-add-button-height, 34px);
    &:focus {
      --product-card-add-button-opacity: 1;
    }
    & .sf-circle-icon:hover {
      --button-box-shadow: 0 4px 11px rgba(29, 31, 34, 0.1);
      --button-box-shadow-opacity: 0.3;
    }
  }
  &__price {
    margin: var(--product-card-margin, 0);
  }
  &__reviews {
    display: flex;
    align-items: center;
  }
  &__rating {
    display: flex;
  }
  &__reviews-count {
    margin: var(--product-card-reviews-count-margin, 0 0 0 var(--spacer-xs));
    color: var(--product-card-reviews-count-color, var(--c-text));
    text-decoration: none;
    @include font(
      --product-reviews-count-font,
      var(--font-weight--light),
      var(--font-size--sm),
      1.6,
      var(--font-family--secondary)
    );
  }
  &__wishlist-icon {
    --icon-color: var(--c-dark-variant);
    position: absolute;
    bottom: var(--product-card-wishlist-icon-bottom, 10px);
    right: var(--product-card-wishlist-icon-right, 10px);
    padding: var(--product-card-wishlist-icon-padding, 0);
    opacity: var(--product-card-wishlist-icon-opacity, 1);
    transition: var(
      --product-card-wishlist-icon-transition,
      opacity 150ms ease-in-out
    );
    cursor: pointer;
    &:focus {
      --product-card-wishlist-icon-opacity: 1;
    }
  }
  &.on-wishlist {
    --product-card-wishlist-icon-opacity: 1;
  }
  @include for-desktop {
    --product-card-transition: box-shadow 150ms ease-in-out;
    --product-card-wishlist-icon-bottom: 20px;
    --product-card-wishlist-icon-right: 20px;
    --product-card-wishlist-icon-opacity: 1;
    --product-card-title-font-size: 16px;
    --product-card-margin: var(--spacer-xs) 0;
    --product-card-add-button-right: calc(50% - 25px);
    --product-card-add-button-bottom: 10px;
    --product-card-add-button-width: 50px;
    --product-card-add-button-height: 50px;
    --product-card-add-button-opacity: 0;
    &__colors-badge {
      margin: var(--spacer-sm) var(--spacer-sm) 0 0;
      width: 1.25rem;
      height:  1.25rem;
      box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
    }
  }
}
</style>
